import { useQuery } from '@tanstack/react-query';
import { Link, useSearch } from '@tanstack/react-router';
import {
  Alert,
  Card,
  Icon,
  NotProvided,
  Page,
  PageHeader,
  Pagination,
  rowLinkClasses,
  Table,
} from '@evogard/ui';
import { formatInstant, formatPercentage, loadFailureMessage } from '@evogard/domain';
import type { CatalogProduct } from '@evogard/contract';
import { MoneyValue } from '../../components/Values.js';
import { api } from '../../lib/api.js';
import { useSessionContext } from '../../lib/session.js';
import { useListSearch, useSearchTerm, useClampedPage } from '../../lib/list-search.js';

/**
 * Os produtos da praça — guincho, rastreador, participação no sinistro —,
 * somados sobre os veículos que os têm.
 *
 * ## A lista é um RECORTE, e a tela diz de quanto
 *
 * O SGA corta a listagem em massa em 3.000 veículos e nega o catálogo de
 * produto ao token da praça, então o produto é perguntado carro a carro: de
 * forma contínua a esteira consulta um lote de veículos, e o "Atualizar do ERP"
 * da ficha consulta um na hora. A base inteira leva dias para a primeira
 * volta. Até lá esta lista é "o que os carros já consultados têm", e o aviso
 * acima da tabela traz os dois números — consultados e total. Sem ele, a
 * contagem de cada linha seria lida como a da base inteira.
 *
 * ## O valor é uma faixa
 *
 * O mesmo produto tem preço por carro. Quando o menor e o maior coincidem a
 * tela mostra um número; quando não, mostra os dois. Reais e percentual nunca
 * se misturam: um percentual passado por `MoneyValue` sairia "R$ 10,00" onde o
 * contrato diz 10%.
 */
export function ProductsScreen() {
  const { data: context } = useSessionContext();
  const tenantId = context?.currentTenant.id;

  /* página, tamanho, busca e filtros na URL — o "voltar" devolve a lista como estava */
  const search = useSearch({ from: '/authenticated/erp/produtos' });
  const list = useListSearch(search);
  const { text, setText } = useSearchTerm(search.q, (q) => list.setFilters({ q }));
  const term = search.q ?? '';
  const { page, perPage } = list;

  const query = useQuery({
    queryKey: ['erp', 'produtos', tenantId, term, page, perPage],
    queryFn: async () => {
      const r = await api.erp.searchProducts({
        body: { term: term.length >= 2 ? term : undefined, page, perPage },
      });
      if (r.status !== 200) {
        throw new Error(loadFailureMessage(r.status, 'os produtos'));
      }
      return r.body;
    },
    placeholderData: (previous, previousQuery) =>
      previousQuery?.queryKey[2] === tenantId ? previous : undefined,
  });

  const result = query.data;
  /* a página além do fim (link antigo, lista que encolheu) vai para a última */
  useClampedPage(page, perPage, result && 'total' in result ? result.total : undefined);
  const neverChecked = result !== undefined && result.vehiclesChecked === 0;

  return (
    <Page measure="cheia">
      <PageHeader title="Produtos">
        O que os veículos de {context?.currentTenant.nome} têm contratado, como o ERP o
        entrega.
      </PageHeader>

      {query.isError && <Alert tone="erro">{query.error.message}</Alert>}

      {result !== undefined && !neverChecked && (
        <Alert tone="informacao">
          Esta lista vem de {result.vehiclesChecked} de {result.vehiclesTotal} veículo(s)
          da base: os que já tiveram os produtos consultados no ERP. A consulta avança sem
          parar, um lote por vez. Um produto que nenhum dos consultados tem não aparece
          aqui, e a contagem de cada linha vale só para eles.
        </Alert>
      )}

      {/* a tabela sai sempre, desde 26/10/2026 — sem consulta, ela diz que não há dados */}
      {(!query.isError || result) && (
        <Card
          title={result === undefined ? 'Carregando…' : `${result.total} produto(s)`}
          descricao={
            result?.lastCheckedAt ? (
              <span className="text-texto-suave">
                consulta mais recente em {formatInstant(result.lastCheckedAt)}
              </span>
            ) : undefined
          }
          bleed
        >
          <Table
            paginate={false}
            search={{
              label: 'Pesquisar produtos',
              placeholder: 'Descrição ou código do produto',
              value: text,
              onChange: setText,
            }}
            label="Produtos do cadastro"
            items={result?.items ?? []}
            loading={query.isLoading}
            rowKey={(p) => p.erpCode}
            /* a seta é montada aqui: `@evogard/ui` não conhece o roteador */
            rowLink={(p) => (
              <Link
                to="/erp/produtos/$code"
                params={{ code: p.erpCode }}
                aria-label={`Ver os veículos com ${p.label ?? `o produto ${p.erpCode}`}`}
                className={rowLinkClasses}
              >
                <Icon nome="seta-direita" size={16} />
              </Link>
            )}
            empty={
              neverChecked
                ? 'Não há dados de produto nesta base.'
                : term
                  ? 'Nenhum produto com essa busca nos veículos consultados.'
                  : 'Os veículos consultados não têm produto ligado no ERP.'
            }
            columns={[
              {
                title: 'Produto',
                alignment: 'inicio' as const,
                minWidth: '18rem',
                cell: (p) => p.label ?? <NotProvided>sem descrição</NotProvided>,
              },
              {
                title: 'Código no ERP',
                minWidth: '8rem',
                cell: (p) => <span className="tabular-nums">{p.erpCode}</span>,
              },
              {
                title: 'Valor',
                alignment: 'fim' as const,
                minWidth: '12rem',
                cell: (p) => <ProductValue product={p} />,
              },
              {
                title: 'Veículos',
                alignment: 'fim' as const,
                minWidth: '7rem',
                cell: (p) => <span className="tabular-nums">{p.vehicles}</span>,
              },
              {
                title: 'Ativo em',
                alignment: 'fim' as const,
                minWidth: '7rem',
                cell: (p) => <span className="tabular-nums">{p.vehiclesActive}</span>,
              },
            ]}
          />

          {result !== undefined && (
            <Pagination
              page={result.page}
              perPage={result.perPage}
              total={result.total}
              onChange={list.setPage}
              onPerPageChange={list.setPerPage}
              label="produtos"
              /* o cartão é `bleed`; o espaçamento é de quem entra nele */
              className="border-t border-borda-suave px-5 py-4"
            />
          )}
        </Card>
      )}
    </Page>
  );
}

/**
 * O valor do produto entre os carros que o têm: um número, ou a faixa.
 *
 * Um produto cobrado em reais num carro e em percentual em outro mostra as
 * duas partes, porque escolher uma esconderia a outra.
 */
function ProductValue({ product: p }: { product: CatalogProduct }) {
  const parts = [];

  if (p.amountMin !== null && p.amountMax !== null) {
    parts.push(
      <span key="reais">
        <MoneyValue value={p.amountMin} />
        {p.amountMin !== p.amountMax && (
          <>
            {' a '}
            <MoneyValue value={p.amountMax} />
          </>
        )}
      </span>,
    );
  }

  if (p.percentageMin !== null && p.percentageMax !== null) {
    parts.push(
      <span key="percentual" className="tabular-nums">
        {formatPercentage(p.percentageMin)}%
        {p.percentageMin !== p.percentageMax &&
          ` a ${formatPercentage(p.percentageMax)}%`}
      </span>,
    );
  }

  if (parts.length === 0) return <NotProvided>o ERP não disse</NotProvided>;
  return (
    <span className="inline-flex flex-wrap justify-end gap-x-2">
      {parts[0]}
      {parts[1] && <span className="text-texto-suave">ou</span>}
      {parts[1]}
    </span>
  );
}
